// src/admin/sections/OverheardSection.jsx
//
// Ticket 38. Admin: Cravings, the "Overheard" view: the second view of the
// Cravings screen (CravingsScreen.jsx). The examples under the craving box
// (ticket 25): write one, switch it showing or off, edit it, delete it.
//
// A READER'S WORDS REACH HERE FROM THE ALL CRAVINGS VIEW (ticket 37), where
// adding the craving is the approval. This view is where the set is judged and
// where one is switched off or taken down. There is no blending switch and no
// reorder: the order is drawn fresh on every load.
//
// THE PREVIEW IS THE HOMEPAGE'S OWN COMPONENT. It draws OverheardRows, fed the
// showing items the way useOverheardItems feeds the homepage: only items that
// are on, only those at or over the 20-word minimum, in a fresh random order.
// It is not a copy of the look, so it cannot drift from it, and switching an
// item off removes it from the preview the way it removes it from the
// homepage. (scripts/check-cravings-admin.mjs fails the build if either end of
// that moves.)
//
// THE WORD RULE. wordsOf and MIN_CRAVING_WORDS come from src/lib/cravingWords.js,
// the count the craving box uses and the table's overheard_items_min_words is
// written to match (20261009120000_overheard_items.sql). An item under 20 words
// can't be saved here, and the database refuses it too; if the two ever
// disagreed, the database's refusal is shown in words, never as a save.
//
// A READER'S WORDS ARE NEVER EDITED. Editing one and still presenting it as
// something a reader said puts words in their mouth, against "Your words stay
// yours" (HowMatchingWorksPage.jsx). Edit is offered only on the items we wrote,
// and the save also filters on source = 'written_by_us', so even a stale screen
// cannot change a reader's words. If a reader's item needs fixing, switch it off
// or delete it.
//
// EVERY WRITE LOOKS AT THE DATABASE'S ANSWER. The screen changes only after the
// row comes back; a refused or empty write is reported, never shown as done. The
// audit lines hold the item's id, never its words.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { MIN_CRAVING_WORDS, wordsOf } from '../../lib/cravingWords';
import { shuffled } from '../../lib/shuffle';
import OverheardRows, { MAX_ITEMS } from '../../components/homepage/OverheardRows';
import { logAudit } from '../lib/auditLog';

const COLUMNS = 'id, text, source, craving_id, is_active, created_at';
const LIST_CAP = 1000;

const SOURCE_LABEL = { written_by_us: 'written by us', reader_words: "a reader's words" };

const TONE = {
  ok:   { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

// What the database said when a save was refused, in words.
export function describeSaveError(error) {
  if (error?.code === '23505') return 'Not saved: Overheard already has an item with exactly these words.';
  if (error?.code === '23514') return `Not saved: the database refused it (an Overheard item needs at least ${MIN_CRAVING_WORDS} words).`;
  return `Not saved: ${error?.message || 'the database refused it'}.`;
}

function Pill({ children, tone = 'neutral' }) {
  const t = TONE[tone] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, disabled, primary, danger, isMobile, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: isMobile ? '0 16px' : '7px 14px', minHeight: isMobile ? 44 : undefined,
        background: primary ? 'var(--brand-forest)' : danger ? 'var(--feedback-error)' : 'var(--brand-white)',
        color: primary || danger ? 'var(--brand-white)' : 'var(--neutral-600)',
        border: primary || danger ? 'none' : '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

// A real switch: on means readers can see it.
function Switch({ on, busy, onToggle, isMobile, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={onToggle}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: isMobile ? 44 : undefined,
        background: 'none', border: 'none', padding: 0, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1, fontFamily: 'var(--font-body)',
      }}
    >
      <span style={{ position: 'relative', width: 38, height: 22, borderRadius: 11, background: on ? 'var(--brand-forest)' : 'var(--neutral-300)', transition: 'background 0.15s', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: 'var(--brand-white)', transition: 'left 0.15s' }} />
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: on ? 'var(--feedback-success)' : 'var(--neutral-500)' }}>{on ? 'Showing' : 'Off'}</span>
    </button>
  );
}

function WordCount({ n }) {
  const enough = n >= MIN_CRAVING_WORDS;
  return (
    <span aria-live="polite" style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: enough ? 'var(--feedback-success)' : 'var(--feedback-warning-ink)' }}>
      {n} of {MIN_CRAVING_WORDS} words{enough ? '' : ` (${MIN_CRAVING_WORDS - n} more to save)`}
    </span>
  );
}

function ItemRow({ item, isMobile, busy, result, editing, confirming, onToggle, onStartEdit, onCancelEdit, onSaveEdit, onAskDelete, onCancelDelete, onDelete }) {
  const [draft, setDraft] = useState(item.text);
  useEffect(() => { if (editing) setDraft(item.text); }, [editing, item.text]);
  const words = wordsOf(item.text);
  const ours = item.source === 'written_by_us';
  const draftWords = wordsOf(draft);
  const canSave = draftWords >= MIN_CRAVING_WORDS && draft.trim() !== item.text.trim();

  return (
    <div data-item={item.id} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10, opacity: item.is_active ? 1 : 0.8 }}>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={4}
            aria-label="Edit the words"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-body)', resize: 'vertical', outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <WordCount n={draftWords} />
            <Btn primary isMobile={isMobile} disabled={busy || !canSave} onClick={() => onSaveEdit(item, draft)}>{busy ? 'Saving…' : 'Save'}</Btn>
            <Btn isMobile={isMobile} disabled={busy} onClick={onCancelEdit}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.text}</p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Pill tone={ours ? 'neutral' : 'ok'}>{SOURCE_LABEL[item.source] || item.source}</Pill>
          <span style={{ fontSize: 'var(--font-size-xs)', color: words >= MIN_CRAVING_WORDS ? 'var(--neutral-500)' : TONE.warn.color, fontWeight: words >= MIN_CRAVING_WORDS ? 400 : 700 }}>
            {words} words{words >= MIN_CRAVING_WORDS ? '' : ` · under ${MIN_CRAVING_WORDS}, readers are not shown it`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Switch on={item.is_active} busy={busy || editing} isMobile={isMobile} onToggle={() => onToggle(item)} label={item.is_active ? 'Switch off' : 'Switch on'} />
          {ours && !editing && <Btn isMobile={isMobile} disabled={busy} onClick={() => onStartEdit(item)}>Edit</Btn>}
          {!confirming && <Btn isMobile={isMobile} disabled={busy || editing} onClick={() => onAskDelete(item)}>Delete</Btn>}
        </div>
      </div>

      {!ours && (
        <div style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
          A reader&apos;s words are never edited. If it needs fixing, switch it off or delete it.
        </div>
      )}

      {confirming && (
        <div role="alert" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)' }}>
          <div style={{ fontWeight: 700 }}>
            Delete this item? It leaves Overheard for good{ours ? '' : ', and the craving stays in the log'}.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn danger isMobile={isMobile} disabled={busy} onClick={() => onDelete(item)}>{busy ? 'Deleting…' : 'Delete it'}</Btn>
            <Btn isMobile={isMobile} disabled={busy} onClick={onCancelDelete}>Keep it</Btn>
          </div>
        </div>
      )}

      {result && (
        <div role="status" style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: TONE[result.tone].bg, color: TONE[result.tone].color, border: `1px solid ${TONE[result.tone].border}` }}>
          {result.text}
        </div>
      )}
    </div>
  );
}

export default function OverheardSection({ isMobile }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [results, setResults] = useState({});
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [draw, setDraw] = useState(0);

  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await supabase
      .from('overheard_items')
      .select(COLUMNS)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(0, LIST_CAP - 1);
    if (error) {
      console.error('[Overheard] list failed:', error.message);
      setLoadError(error.message);
      setItems([]);
      setLoading(false);
      return;
    }
    setItems(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setBusy = (id, on) => setBusyIds(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const setResult = (id, r) => setResults(prev => ({ ...prev, [id]: r }));
  const patchItem = (id, patch) => setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));

  // What the homepage would draw: showing items at or over the minimum, in a
  // fresh random order. The ORDER is drawn again when the showing set changes
  // or "Draw again" is pressed; the WORDS are always read from the current
  // items, so an edit shows in the preview at once without reshuffling it.
  const usable = items.filter(i => i.is_active && wordsOf(i.text) >= MIN_CRAVING_WORDS);
  const showingKey = usable.map(i => i.id).sort((a, b) => a - b).join(',');
  const drawnOrder = useMemo(
    () => shuffled(usable).map(i => i.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showingKey, draw]
  );
  const usableById = new Map(usable.map(i => [i.id, i]));
  const drawn = drawnOrder
    .map(id => usableById.get(id))
    .filter(Boolean)
    .map(i => ({ id: i.id, text: i.text, source: i.source }));
  const showingCount = drawn.length;
  const offCount = items.filter(i => !i.is_active).length;
  const oursCount = items.filter(i => i.source === 'written_by_us').length;
  const readersCount = items.length - oursCount;

  async function addOne() {
    const clean = newText.trim();
    if (wordsOf(clean) < MIN_CRAVING_WORDS) {
      setAddResult({ tone: 'warn', text: `Not saved: it is ${wordsOf(clean)} words, and an Overheard example needs at least ${MIN_CRAVING_WORDS}.` });
      return;
    }
    setAdding(true);
    setAddResult(null);
    const { data: saved, error } = await supabase
      .from('overheard_items')
      .insert({ text: clean, source: 'written_by_us', is_active: true })
      .select(COLUMNS)
      .single();
    if (error || !saved) {
      setAddResult({ tone: 'bad', text: describeSaveError(error) });
      setAdding(false);
      return;
    }
    await logAudit('create', 'overheard_item', `Wrote Overheard item #${saved.id}`, saved.id);
    setItems(prev => [saved, ...prev]);
    setNewText('');
    setAddResult({ tone: 'ok', text: 'Saved. It is showing on Overheard now.' });
    setAdding(false);
  }

  async function toggle(item) {
    const next = !item.is_active;
    setBusy(item.id, true);
    setResult(item.id, null);
    const { data: saved, error } = await supabase
      .from('overheard_items')
      .update({ is_active: next })
      .eq('id', item.id)
      .select('id, is_active');
    if (error) {
      setResult(item.id, { tone: 'bad', text: `Not changed: ${error.message}` });
      setBusy(item.id, false);
      return;
    }
    if (!saved?.length) {
      // The row is gone (another admin deleted it): say so and show the truth.
      setItems(prev => prev.filter(i => i.id !== item.id));
      setBusy(item.id, false);
      setAddResult({ tone: 'warn', text: 'That item no longer exists (someone deleted it). The list has been updated.' });
      return;
    }
    await logAudit('update', 'overheard_item', `Switched Overheard item #${item.id} ${saved[0].is_active ? 'on' : 'off'}`, item.id);
    patchItem(item.id, { is_active: saved[0].is_active });
    setResult(item.id, { tone: 'ok', text: saved[0].is_active ? 'Switched on. Readers can see it.' : 'Switched off. Readers no longer see it.' });
    setBusy(item.id, false);
  }

  async function saveEdit(item, draft) {
    const clean = draft.trim();
    if (item.source !== 'written_by_us') {
      setResult(item.id, { tone: 'warn', text: "A reader's words are never edited." });
      return;
    }
    if (wordsOf(clean) < MIN_CRAVING_WORDS) {
      setResult(item.id, { tone: 'warn', text: `Not saved: it is ${wordsOf(clean)} words, and an Overheard example needs at least ${MIN_CRAVING_WORDS}.` });
      return;
    }
    setBusy(item.id, true);
    setResult(item.id, null);
    // source in the filter: a reader's words can't be changed even from a stale screen.
    const { data: saved, error } = await supabase
      .from('overheard_items')
      .update({ text: clean })
      .eq('id', item.id)
      .eq('source', 'written_by_us')
      .select('id, text');
    if (error) {
      setResult(item.id, { tone: 'bad', text: describeSaveError(error) });
      setBusy(item.id, false);
      return;
    }
    if (!saved?.length) {
      setResult(item.id, { tone: 'warn', text: 'Not saved: the item was not found, or it is not one we wrote. Reload the list.' });
      setBusy(item.id, false);
      return;
    }
    await logAudit('update', 'overheard_item', `Edited Overheard item #${item.id}`, item.id);
    patchItem(item.id, { text: saved[0].text });
    setEditingId(null);
    setResult(item.id, { tone: 'ok', text: 'Saved.' });
    setBusy(item.id, false);
  }

  async function remove(item) {
    setBusy(item.id, true);
    setResult(item.id, null);
    const { data: gone, error } = await supabase
      .from('overheard_items')
      .delete()
      .eq('id', item.id)
      .select('id');
    if (error) {
      setResult(item.id, { tone: 'bad', text: `Not deleted: ${error.message}` });
      setConfirmId(null);
      setBusy(item.id, false);
      return;
    }
    if (!gone?.length) {
      // Nothing was deleted: it was already gone. Show that, not "deleted".
      setItems(prev => prev.filter(i => i.id !== item.id));
      setConfirmId(null);
      setBusy(item.id, false);
      setAddResult({ tone: 'warn', text: 'That item was already gone. The list has been updated.' });
      return;
    }
    await logAudit('delete', 'overheard_item', `Deleted Overheard item #${item.id}`, item.id);
    setItems(prev => prev.filter(i => i.id !== item.id));
    setConfirmId(null);
    setBusy(item.id, false);
    setAddResult({ tone: 'ok', text: 'Deleted. It is no longer on Overheard.' });
  }

  const newWords = wordsOf(newText);

  return (
    <div style={{ fontFamily: 'var(--font-body)' }}>
      {/* ── Preview ─────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '18px 22px', marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>What readers see</div>
          <Btn isMobile={isMobile} onClick={() => setDraw(d => d + 1)}>Draw again</Btn>
        </div>
        <div style={{ background: 'var(--brand-cream-page)', borderRadius: 'var(--radius-md)', padding: isMobile ? '0 10px 10px' : '0 16px 14px' }}>
          {loading ? (
            <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)' }}>Loading…</div>
          ) : showingCount < 2 ? (
            <div role="status" style={{ padding: '18px 0', color: TONE.warn.color, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
              Fewer than two items are showing, so readers see no Overheard row at all.
            </div>
          ) : (
            <OverheardRows items={drawn} isMobile={isMobile} />
          )}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          This is the homepage&apos;s own row, drawn the way a reader&apos;s visit draws it: only items that are showing, in a fresh order each time.
          {showingCount > MAX_ITEMS && ` A reader sees ${MAX_ITEMS} of the ${showingCount} showing; Draw again to see another set.`}
          {' '}Tapping a row does nothing here. Judge the set, not single lines: is it spread across genres, and does each one say a feeling and an ending?
        </p>
        {!loading && !loadError && (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
            {items.length} item{items.length === 1 ? '' : 's'}: {oursCount} written by us, {readersCount} a reader&apos;s words · {showingCount} showing, {offCount} off.
          </p>
        )}
      </div>

      {/* ── Write one ───────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '18px 22px', marginBottom: 18 }}>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Write one</div>
        <p style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          Describe a feeling and an ending, not a genre, and spread the set across genres.
        </p>
        <textarea
          value={newText}
          onChange={e => setNewText(e.target.value)}
          rows={3}
          aria-label="Write a new example"
          placeholder="Write it the way a reader would say it."
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-body)', resize: 'vertical', outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <WordCount n={newWords} />
          <Btn primary isMobile={isMobile} disabled={adding || newWords < MIN_CRAVING_WORDS} onClick={addOne}>{adding ? 'Saving…' : 'Add to Overheard'}</Btn>
        </div>
        {addResult && (
          <div role="status" style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: TONE[addResult.tone].bg, color: TONE[addResult.tone].color, border: `1px solid ${TONE[addResult.tone].border}` }}>
            {addResult.text}
          </div>
        )}
      </div>

      {/* ── The set ─────────────────────────────────────────────────────── */}
      {loading ? null : loadError ? (
        <div role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          Couldn&apos;t load the set: {loadError}
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          Nothing on Overheard yet. Write one above, or add a craving from All cravings.
        </div>
      ) : (
        <>
          {items.length >= LIST_CAP && (
            <div role="status" style={{ marginBottom: 10, fontSize: 'var(--font-size-xs)', color: TONE.warn.color, fontWeight: 600 }}>
              Showing the newest {LIST_CAP.toLocaleString()} items only.
            </div>
          )}
          {items.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              isMobile={isMobile}
              busy={busyIds.has(item.id)}
              result={results[item.id]}
              editing={editingId === item.id}
              confirming={confirmId === item.id}
              onToggle={toggle}
              onStartEdit={i => { setEditingId(i.id); setConfirmId(null); setResult(i.id, null); }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={saveEdit}
              onAskDelete={i => { setConfirmId(i.id); setEditingId(null); setResult(i.id, null); }}
              onCancelDelete={() => setConfirmId(null)}
              onDelete={remove}
            />
          ))}
        </>
      )}
    </div>
  );
}
